import type { Client, ClientNeedsTag } from '@/lib/types';
import type { OverallClassification } from '@/lib/posture-analysis/types';
import { getClient, updateClient } from '@/components/clients/services/clients-api';
import {
  dedupeNeedsTags,
  drillSuggestionFiltersToNeedsTags,
  needsTagKey,
  seedNeedsFromClassification,
} from '@/lib/clients/needs-tags';
import { buildDrillSuggestionFilters } from '@/lib/posture-analysis/drill-suggestion-filters';
import { normalizePainLevelNrs } from '@/lib/clients/roster-fields';
import logger from '@/lib/logging';

/**
 * Attribution stamped on `needsTagsUpdatedBy` when needs are auto-derived from a
 * classification refresh, as opposed to a practitioner edit (`ClientNeedsEditor`,
 * the Drill Library write-back bound mode), which stamp the acting user's
 * email/uid. Reuses the existing `needsTagsUpdatedAt/By` audit fields (Phase 2B
 * write-back) rather than inventing a new schema.
 */
export const NEEDS_AUTO_DERIVE_SOURCE = 'system:classification-refresh';

/**
 * Derive needs tags for a freshly classified client.
 *
 * When the full {@link OverallClassification} is available (the posture/SOP save
 * paths keep it in hand — see `computeClientClassificationDetail*`), derivation
 * runs the same `buildDrillSuggestionFilters` → `drillSuggestionFiltersToNeedsTags`
 * pipeline that powers on-demand seeding. This is what surfaces Biomechanical
 * Intent needs like "Anterior Expansion" (Mathias's "anterior ribcage expansion")
 * and Movement Limitation needs from `overall.movementLimitations` — signals the
 * short classification string can never carry.
 *
 * The string-only derivation (`seedNeedsFromClassification` over the fresh client
 * with the new classification) is always unioned in as a floor: it is the whole
 * result for callers that only have the string, and it covers the SOP edge where
 * the persisted short code's pattern was backfilled from `previousClassification`
 * while `overall.pattern` stayed `'N'`.
 */
function deriveNeedsForClassification(
  freshClient: Client,
  shortClassification: string,
  overall?: OverallClassification | null
): ClientNeedsTag[] {
  const fromString = seedNeedsFromClassification({
    ...freshClient,
    classification: shortClassification,
  });

  if (!overall) return fromString;

  // Defensive: movementLimitations is typed as required on the server response,
  // but never let a malformed payload throw the whole derivation away.
  const filters = buildDrillSuggestionFilters(
    {
      ...overall,
      movementLimitations: Array.isArray(overall.movementLimitations)
        ? overall.movementLimitations
        : [],
    },
    { liftingExperience: freshClient.liftingExperience, painLevel: normalizePainLevelNrs(freshClient) }
  );

  return dedupeNeedsTags([
    ...drillSuggestionFiltersToNeedsTags(filters),
    { category: 'Phase', value: filters.phaseTag },
    ...fromString,
  ]);
}

/**
 * Auto-derive needs tags from a freshly computed classification and additively
 * merge them into the client's current `needsTags`.
 *
 * Mathias's ask: "typical needs like anterior ribcage expansion should be set by
 * the posture or sop assessments. Or ... auto derived from classification." This
 * reuses the same seeding machinery as the on-demand suggestion flow in
 * `ClientNeedsEditor` — no new mapping is introduced.
 *
 * Contract (mirrors `refreshClientClassificationFrom*`):
 * - Additive only: derived tags are unioned into the client's existing needs by
 *   canonical `category::value` key. Practitioner-curated needs are NEVER removed
 *   or overwritten.
 * - No-op when every derived tag is already present (no churn).
 * - Fetches the client's CURRENT needsTags fresh immediately before merging
 *   (not a caller-held/stale object). The clients PATCH endpoint replaces
 *   `needsTags` wholesale (no server-side array-union), so a tight read-merge-write
 *   window is the safest available strategy without a dedicated merge endpoint.
 * - `shortClassification` falsy (classification compute failed) ⇒ derive nothing.
 * - Failure-safe: swallow + log. Must never block/fail the calling posture/SOP
 *   save. Callers should invoke this fire-and-forget, piggybacked on the same
 *   recency-guarded call sites as the classification refresh (do not call this
 *   from an unguarded path).
 */
export async function deriveAndMergeClientNeeds(
  clientId: string,
  shortClassification: string | null | undefined,
  overall?: OverallClassification | null
): Promise<void> {
  if (!shortClassification) return;

  try {
    const freshClient = await getClient(clientId);
    if (!freshClient) return;

    const derived = deriveNeedsForClassification(freshClient, shortClassification, overall);
    if (derived.length === 0) return;

    const existing = freshClient.needsTags ?? [];
    const existingKeys = new Set(existing.map(needsTagKey));
    const hasNewTag = derived.some((tag) => !existingKeys.has(needsTagKey(tag)));
    if (!hasNewTag) return;

    const merged = dedupeNeedsTags([...existing, ...derived]);
    await updateClient(clientId, {
      needsTags: merged,
      needsTagsUpdatedAt: new Date().toISOString(),
      needsTagsUpdatedBy: NEEDS_AUTO_DERIVE_SOURCE,
    });
  } catch (error) {
    logger.warn(`Failed to auto-derive needs tags for ${clientId}`, error);
  }
}
